import {
  BaseImageOrder,
  BaseScreenPageItem,
  BaseScreenSpecs,
  BaseScreenUploadOptions,
} from '../../types/base'
import { FeatureBase } from '../FeatureBase'

declare module '../FeatureMap' {
  export interface DeviceFeatureMap {
    /** 底座屏幕能力。无屏底座也会挂载，但部分方法会抛 NOT_SUPPORTED。 */
    baseScreen: IBaseScreenFeature
  }
}

export interface IBaseScreenFeature {
  /** 屏显页面业务编排 API，不和底层协议读写方法混放。 */
  pages: IBaseScreenPages
  /** 读取屏幕硬件规格位图。 */
  getSpecs(): Promise<BaseScreenSpecs>
  /** 读取指定页面的图片名称。 */
  getImageName(index: number): Promise<string>
  /** 写入指定自定义页面的图片名称，最多写入 16 字节 UTF-8。系统页面会被拒绝。 */
  setImageName(index: number, name: string): Promise<void>
  /** 删除指定自定义页面的图片素材。系统页面会被拒绝。 */
  deleteImage(index: number): Promise<void>
  /** 读取屏幕亮度。 */
  getScreenBrightness(): Promise<number>
  /** 设置屏幕亮度，当前约定范围 0-100。 */
  setScreenBrightness(brightness: number): Promise<void>
  /** 读取屏幕轮播数量和页面顺序。 */
  getImageOrder(): Promise<BaseImageOrder>
  /** 写入屏幕轮播数量和页面顺序。系统页固定在前，order 会补齐/截断到 8 项。 */
  setImageOrder(showNum: number, order: number[] | Uint8Array): Promise<void>
  /** 读取每个页面的轮播停留时间，单位秒。 */
  getImageTimes(): Promise<number[]>
  /** 设置指定页面的轮播停留时间，范围 3-65535 秒。 */
  setImageTime(index: number, seconds: number): Promise<void>
  /** 读取首页样式。 */
  getIndexStyle(): Promise<number>
  /** 设置首页样式，当前固件范围 0-2。 */
  setIndexStyle(style: number): Promise<void>
  /** 重置屏幕芯片，恢复出厂前需要先调用。 */
  resetScreenChip(): Promise<void>
  /** 上传单张图片到自定义页面。系统页面会被拒绝，SDK 会转 RGB565。 */
  uploadStaticImage(
    index: number,
    imageData: ImageData,
    options?: BaseScreenUploadOptions,
  ): Promise<void>
  /** 上传 GIF 帧序列到自定义页面，最多发送前 24 帧。系统页面会被拒绝。 */
  uploadGifFrames(
    index: number,
    frames: ImageData[],
    options?: BaseScreenUploadOptions,
  ): Promise<void>
  /** 从 File 自动识别静态图/GIF 并上传到自定义页面。系统页面会被拒绝。 */
  uploadFile(index: number, file: File, options?: BaseScreenUploadOptions): Promise<void>
}

export interface IBaseScreenPages {
  /** 读取屏显页面业务列表，长度由 manifest 决定，数组顺序即播放顺序。 */
  getPages(): Promise<BaseScreenPageItem[]>
  /** 写入屏显页面业务列表，SDK 会转换为名称、轮播顺序和停留时间。 */
  setPages(pages: BaseScreenPageItem[]): Promise<void>
}

/** 底座屏幕 feature 抽象类，具体实现位于 chips/shared/feature-impls/base-screen。 */
export class BaseScreenFeature extends FeatureBase implements IBaseScreenFeature {
  pages: IBaseScreenPages = {
    getPages: () => this.unsupported('BaseScreenFeature.pages.getPages'),
    setPages: (_pages: BaseScreenPageItem[]) =>
      this.unsupported('BaseScreenFeature.pages.setPages'),
  }

  getSpecs(): Promise<BaseScreenSpecs> {
    return this.unsupported('BaseScreenFeature.getSpecs')
  }
  getImageName(_index: number): Promise<string> {
    return this.unsupported('BaseScreenFeature.getImageName')
  }
  setImageName(_index: number, _name: string): Promise<void> {
    return this.unsupported('BaseScreenFeature.setImageName')
  }
  deleteImage(_index: number): Promise<void> {
    return this.unsupported('BaseScreenFeature.deleteImage')
  }
  getScreenBrightness(): Promise<number> {
    return this.unsupported('BaseScreenFeature.getScreenBrightness')
  }
  setScreenBrightness(_brightness: number): Promise<void> {
    return this.unsupported('BaseScreenFeature.setScreenBrightness')
  }
  getImageOrder(): Promise<BaseImageOrder> {
    return this.unsupported('BaseScreenFeature.getImageOrder')
  }
  setImageOrder(_showNum: number, _order: number[] | Uint8Array): Promise<void> {
    return this.unsupported('BaseScreenFeature.setImageOrder')
  }
  getImageTimes(): Promise<number[]> {
    return this.unsupported('BaseScreenFeature.getImageTimes')
  }
  setImageTime(_index: number, _seconds: number): Promise<void> {
    return this.unsupported('BaseScreenFeature.setImageTime')
  }
  getIndexStyle(): Promise<number> {
    return this.unsupported('BaseScreenFeature.getIndexStyle')
  }
  setIndexStyle(_style: number): Promise<void> {
    return this.unsupported('BaseScreenFeature.setIndexStyle')
  }
  resetScreenChip(): Promise<void> {
    return this.unsupported('BaseScreenFeature.resetScreenChip')
  }
  uploadStaticImage(
    _index: number,
    _imageData: ImageData,
    _options?: BaseScreenUploadOptions,
  ): Promise<void> {
    return this.unsupported('BaseScreenFeature.uploadStaticImage')
  }
  uploadGifFrames(
    _index: number,
    _frames: ImageData[],
    _options?: BaseScreenUploadOptions,
  ): Promise<void> {
    return this.unsupported('BaseScreenFeature.uploadGifFrames')
  }
  uploadFile(_index: number, _file: File, _options?: BaseScreenUploadOptions): Promise<void> {
    return this.unsupported('BaseScreenFeature.uploadFile')
  }
}
